import { useState, useEffect, useRef } from "react";
import { useLanguage } from "../contexts/LanguageContext";
import { useUpdateWorkspaceMutation } from "../store/api/workspaces";
import { toast } from "sonner";
import { handleApiError } from "../utils/apiErrors";
import { Workspace } from "../types";
import * as yup from "yup";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { Label } from "./ui/label";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "./ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "./ui/popover";
import { Loader2, Check, ChevronsUpDown } from "lucide-react";
import { cn } from "../utils/cn";

const TIMEZONES = [
  // UTC
  { value: "UTC", label: "UTC (GMT+0)" },
  // Africa
  { value: "Africa/Cairo", label: "Africa/Cairo (GMT+2)" },
  { value: "Africa/Casablanca", label: "Africa/Casablanca (GMT+1)" },
  { value: "Africa/Johannesburg", label: "Africa/Johannesburg (GMT+2)" },
  { value: "Africa/Lagos", label: "Africa/Lagos (GMT+1)" },
  { value: "Africa/Nairobi", label: "Africa/Nairobi (GMT+3)" },
  // America
  { value: "America/Anchorage", label: "America/Anchorage (GMT-9)" },
  { value: "America/Argentina/Buenos_Aires", label: "America/Buenos_Aires (GMT-3)" },
  { value: "America/Bogota", label: "America/Bogota (GMT-5)" },
  { value: "America/Chicago", label: "America/Chicago (GMT-6)" },
  { value: "America/Denver", label: "America/Denver (GMT-7)" },
  { value: "America/Halifax", label: "America/Halifax (GMT-4)" },
  { value: "America/Lima", label: "America/Lima (GMT-5)" },
  { value: "America/Los_Angeles", label: "America/Los_Angeles (GMT-8)" },
  { value: "America/Mexico_City", label: "America/Mexico_City (GMT-6)" },
  { value: "America/New_York", label: "America/New_York (GMT-5)" },
  { value: "America/Phoenix", label: "America/Phoenix (GMT-7)" },
  { value: "America/Santiago", label: "America/Santiago (GMT-4)" },
  { value: "America/Sao_Paulo", label: "America/Sao_Paulo (GMT-3)" },
  { value: "America/Toronto", label: "America/Toronto (GMT-5)" },
  { value: "America/Vancouver", label: "America/Vancouver (GMT-8)" },
  // Asia
  { value: "Asia/Almaty", label: "Asia/Almaty (GMT+6)" },
  { value: "Asia/Baghdad", label: "Asia/Baghdad (GMT+3)" },
  { value: "Asia/Bangkok", label: "Asia/Bangkok (GMT+7)" },
  { value: "Asia/Colombo", label: "Asia/Colombo (GMT+5:30)" },
  { value: "Asia/Dhaka", label: "Asia/Dhaka (GMT+6)" },
  { value: "Asia/Dubai", label: "Asia/Dubai (GMT+4)" },
  { value: "Asia/Ho_Chi_Minh", label: "Asia/Ho_Chi_Minh (GMT+7)" },
  { value: "Asia/Hong_Kong", label: "Asia/Hong_Kong (GMT+8)" },
  { value: "Asia/Jakarta", label: "Asia/Jakarta (GMT+7)" },
  { value: "Asia/Jerusalem", label: "Asia/Jerusalem (GMT+2)" },
  { value: "Asia/Karachi", label: "Asia/Karachi (GMT+5)" },
  { value: "Asia/Kathmandu", label: "Asia/Kathmandu (GMT+5:45)" },
  { value: "Asia/Kolkata", label: "Asia/Kolkata (GMT+5:30)" },
  { value: "Asia/Kuala_Lumpur", label: "Asia/Kuala_Lumpur (GMT+8)" },
  { value: "Asia/Kuwait", label: "Asia/Kuwait (GMT+3)" },
  { value: "Asia/Manila", label: "Asia/Manila (GMT+8)" },
  { value: "Asia/Riyadh", label: "Asia/Riyadh (GMT+3)" },
  { value: "Asia/Seoul", label: "Asia/Seoul (GMT+9)" },
  { value: "Asia/Shanghai", label: "Asia/Shanghai (GMT+8)" },
  { value: "Asia/Singapore", label: "Asia/Singapore (GMT+8)" },
  { value: "Asia/Taipei", label: "Asia/Taipei (GMT+8)" },
  { value: "Asia/Tehran", label: "Asia/Tehran (GMT+3:30)" },
  { value: "Asia/Tokyo", label: "Asia/Tokyo (GMT+9)" },
  { value: "Asia/Yangon", label: "Asia/Yangon (GMT+6:30)" },
  // Atlantic
  { value: "Atlantic/Azores", label: "Atlantic/Azores (GMT-1)" },
  { value: "Atlantic/Reykjavik", label: "Atlantic/Reykjavik (GMT+0)" },
  // Australia
  { value: "Australia/Adelaide", label: "Australia/Adelaide (GMT+9:30)" },
  { value: "Australia/Brisbane", label: "Australia/Brisbane (GMT+10)" },
  { value: "Australia/Darwin", label: "Australia/Darwin (GMT+9:30)" },
  { value: "Australia/Melbourne", label: "Australia/Melbourne (GMT+10)" },
  { value: "Australia/Perth", label: "Australia/Perth (GMT+8)" },
  { value: "Australia/Sydney", label: "Australia/Sydney (GMT+10)" },
  // Europe
  { value: "Europe/Amsterdam", label: "Europe/Amsterdam (GMT+1)" },
  { value: "Europe/Athens", label: "Europe/Athens (GMT+2)" },
  { value: "Europe/Berlin", label: "Europe/Berlin (GMT+1)" },
  { value: "Europe/Brussels", label: "Europe/Brussels (GMT+1)" },
  { value: "Europe/Budapest", label: "Europe/Budapest (GMT+1)" },
  { value: "Europe/Copenhagen", label: "Europe/Copenhagen (GMT+1)" },
  { value: "Europe/Dublin", label: "Europe/Dublin (GMT+0)" },
  { value: "Europe/Helsinki", label: "Europe/Helsinki (GMT+2)" },
  { value: "Europe/Istanbul", label: "Europe/Istanbul (GMT+3)" },
  { value: "Europe/Lisbon", label: "Europe/Lisbon (GMT+0)" },
  { value: "Europe/London", label: "Europe/London (GMT+0)" },
  { value: "Europe/Madrid", label: "Europe/Madrid (GMT+1)" },
  { value: "Europe/Moscow", label: "Europe/Moscow (GMT+3)" },
  { value: "Europe/Oslo", label: "Europe/Oslo (GMT+1)" },
  { value: "Europe/Paris", label: "Europe/Paris (GMT+1)" },
  { value: "Europe/Prague", label: "Europe/Prague (GMT+1)" },
  { value: "Europe/Rome", label: "Europe/Rome (GMT+1)" },
  { value: "Europe/Stockholm", label: "Europe/Stockholm (GMT+1)" },
  { value: "Europe/Vienna", label: "Europe/Vienna (GMT+1)" },
  { value: "Europe/Warsaw", label: "Europe/Warsaw (GMT+1)" },
  { value: "Europe/Zurich", label: "Europe/Zurich (GMT+1)" },
  // Pacific
  { value: "Pacific/Auckland", label: "Pacific/Auckland (GMT+12)" },
  { value: "Pacific/Fiji", label: "Pacific/Fiji (GMT+12)" },
  { value: "Pacific/Guam", label: "Pacific/Guam (GMT+10)" },
  { value: "Pacific/Honolulu", label: "Pacific/Honolulu (GMT-10)" },
  { value: "Pacific/Samoa", label: "Pacific/Samoa (GMT-11)" },
];

interface EditWorkspaceModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  workspace: Workspace;
}

export default function EditWorkspaceModal({
  open,
  onOpenChange,
  workspace,
}: EditWorkspaceModalProps) {
  const { t } = useLanguage();
  const dialogContentRef = useRef<HTMLDivElement>(null);
  const [updateWorkspace, { isLoading }] = useUpdateWorkspaceMutation();
  const [timezoneOpen, setTimezoneOpen] = useState(false);
  
  const [formData, setFormData] = useState({
    name: workspace.name,
    timezone: workspace.timezone || '',
    fiscalYearStartMonth: workspace.fiscalYearStartMonth ?? null,
    weekStartDay: workspace.weekStartDay ?? null,
  });

  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  useEffect(() => {
    setFormData({
      name: workspace.name,
      timezone: workspace.timezone || '',
      fiscalYearStartMonth: workspace.fiscalYearStartMonth ?? null,
      weekStartDay: workspace.weekStartDay ?? null,
    });
    // Clear errors when modal opens/closes or workspace changes
    setErrors({});
  }, [workspace, open]);

  // Yup validation schema
  const validationSchema = yup.object().shape({
    name: yup
      .string()
      .required(t('workspace.validation.nameRequired'))
      .max(50, t('workspace.validation.nameMaxLength')),
  });

  const validateForm = async (): Promise<boolean> => {
    try {
      await validationSchema.validate(formData, { abortEarly: false });
      setErrors({});
      return true;
    } catch (err) {
      if (err instanceof yup.ValidationError) {
        const newErrors: { [key: string]: string } = {};
        err.inner.forEach((error) => {
          if (error.path) {
            newErrors[error.path] = error.message;
          }
        });
        setErrors(newErrors);
      }
      return false;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate form
    const isValid = await validateForm();
    if (!isValid) {
      return;
    }

    try {
      await updateWorkspace({
        id: workspace.id,
        data: {
          name: formData.name,
          timezone: formData.timezone || undefined,
          fiscalYearStartMonth: formData.fiscalYearStartMonth ?? undefined,
          weekStartDay: formData.weekStartDay ?? undefined,
        }
      }).unwrap();

      toast.success(t('workspace.updateSuccess'));
      onOpenChange(false);
    } catch (error) {
      handleApiError(error, t('workspace.updateFailed'));
    }
  };

  // Scroll to top when modal opens
  useEffect(() => {
    if (open && dialogContentRef.current) {
      dialogContentRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent ref={dialogContentRef} className="max-w-md">
        <DialogHeader>
          <DialogTitle>{t('workspace.editWorkspace')}</DialogTitle>
          <DialogDescription>
            {t('workspace.workspaceSettings')}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label className="mb-2" htmlFor="name">{t('workspace.workspaceName')} *</Label>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) => {
                setFormData({ ...formData, name: e.target.value });
                // Clear error when user types
                if (errors.name) {
                  setErrors({ ...errors, name: '' });
                }
              }}
              className={errors.name ? "border-red-500" : ""}
              maxLength={50}
              required
            />
            {errors.name && (
              <p className="mt-1 text-sm text-red-500">{errors.name}</p>
            )}
          </div>

          <div>
            <Label className="mb-2" htmlFor="timezone">{t('settings.timezone')}</Label>
            <Popover open={timezoneOpen} onOpenChange={setTimezoneOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  role="combobox"
                  aria-expanded={timezoneOpen}
                  className="w-full justify-between font-normal"
                >
                  {formData.timezone
                    ? TIMEZONES.find((tz) => tz.value === formData.timezone)?.label || formData.timezone
                    : t('settings.timezonePlaceholder')}
                  <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[400px] p-0" align="start">
                <Command>
                  <CommandInput placeholder={t('settings.searchTimezone')} />
                  <CommandEmpty>{t('settings.noTimezoneFound')}</CommandEmpty>
                  <CommandGroup className="max-h-[300px] overflow-y-auto">
                    {TIMEZONES.map((tz) => (
                      <CommandItem
                        key={tz.value}
                        value={tz.label}
                        onSelect={() => {
                          setFormData({ ...formData, timezone: tz.value });
                          setTimezoneOpen(false);
                        }}
                      >
                        <Check
                          className={cn(
                            "mr-2 h-4 w-4",
                            formData.timezone === tz.value ? "opacity-100" : "opacity-0"
                          )}
                        />
                        {tz.label}
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </Command>
              </PopoverContent>
            </Popover>
          </div>

          <div>
            <Label className="mb-2" htmlFor="fiscalYearStartMonth">{t('settings.fiscalYearStart')}</Label>
            <Select
              value={formData.fiscalYearStartMonth !== null ? String(formData.fiscalYearStartMonth) : undefined}
              onValueChange={(value) =>
                setFormData({
                  ...formData,
                  fiscalYearStartMonth: parseInt(value),
                })
              }
            >
              <SelectTrigger>
                <SelectValue placeholder={t('settings.fiscalYearStartPlaceholder')} />
              </SelectTrigger>
              <SelectContent>
                {Array.from({ length: 12 }, (_, i) => i + 1).map((month) => (
                  <SelectItem key={month} value={String(month)}>
                    {month} {t('settings.month')}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="mb-2" htmlFor="weekStartDay">{t('settings.weekStartDay')}</Label>
            <Select
              value={formData.weekStartDay !== null ? String(formData.weekStartDay) : undefined}
              onValueChange={(value) =>
                setFormData({ ...formData, weekStartDay: parseInt(value) })
              }
            >
              <SelectTrigger>
                <SelectValue placeholder={t('settings.weekStartDayPlaceholder')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="0">{t('settings.sunday')}</SelectItem>
                <SelectItem value="1">{t('settings.monday')}</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="flex justify-end gap-2 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isLoading}
            >
              {t('common.cancel')}
            </Button>
            <Button type="submit" disabled={isLoading}>
              {isLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {t('common.save')}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
